import { faCircleExclamation, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import L, { Map as MapProp } from "leaflet";
import LF from "leaflet-geometryutil";
import "leaflet-geosearch/assets/css/leaflet.css";
import "leaflet-providers";
import "leaflet/dist/leaflet.css";
import { ChangeEvent, KeyboardEvent, SyntheticEvent, useEffect, useMemo, useReducer, useState } from "react";
import { MapContainer, Marker, useMapEvents } from "react-leaflet";
import { selectCommunicationData } from "../../store";
import { useAppSelector } from "../../store/hooks";
import { isPositionPresent } from "../../utils/utils";
import ConfirmCancelBtn from "../ActionsBtn/ConfirmCancelBtn";
import { MapPageContainer } from "../styled/Map.style";
import MapMarkerImg from './../../assets/img/map-marker.png';
import { mapAddressFinderReducer, MapStateActions } from "./MapAddressFinderReducer";

// Funzione helper per calcolare distanza in metri tra due coordinate (Formula di Haversine)
export const getDistanceMeters = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const toRad = (v: number) => v * Math.PI / 180;
    const R = 6371000; // Raggio della Terra in metri
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

// Funzione helper per parsing response HERE API
export const getLatLonFromHereResponse = (responseData: any) => {
    const items = responseData?.items;
    if (items && items.length > 0) {
        const firstItem = items[0];
        return {
            Latitude: firstItem.position?.lat,
            Longitude: firstItem.position?.lng,
            AddressLabel: firstItem.address?.label || firstItem.title,
        };
    }
    return {
        Latitude: null,
        Longitude: null,
        AddressLabel: null,
    };
};

// Funzione helper per parsing response HERE Lookup API
export const getLatLonFromLookupResponse = (responseData: any) => {
    if (responseData && responseData.position) {
        return {
            Latitude: responseData.position.lat,
            Longitude: responseData.position.lng,
            AddressLabel: responseData.address?.label || responseData.title,
        };
    }
    return {
        Latitude: null,
        Longitude: null,
        AddressLabel: null,
    };
};

// Funzione helper per estrarre ID da suggestion (compatibilità multipla)
export const extractSuggestionId = (suggestion: any) => {
    if (!suggestion) return undefined;
    return suggestion.locationId || suggestion.id || suggestion['@id'];
};

interface MapCmpProps {
    displayOnly: boolean;
    fixedMarkerDropMap: boolean;
    withSearch: boolean;
    hasProximityWarning: boolean;
    maxDistance?: number;
    zoom?: number;
    classes?: string;
    manageBtnPresent?: boolean;
    onCancelHandler?: any;
    onConfirmHandler?: any;
}

/* istanbul ignore next */
const MAX_DISTANCE = 1500;

export const MapAddressFinder = (props: MapCmpProps) => {
    /* istanbul ignore next */
    const { displayOnly, fixedMarkerDropMap, withSearch, hasProximityWarning, maxDistance, zoom, classes, manageBtnPresent, onCancelHandler, onConfirmHandler } = props;
    /* istanbul ignore next */
    const [mapRef, setMap] = useState<MapProp | null>(null);
    /* istanbul ignore next */
    const communicationData = useAppSelector(selectCommunicationData)
    /* istanbul ignore next */
    const { communication } = communicationData;

    const stateInitValues = {
        targetPosition: null,
        address: '', // inc_luogo
        suggestions: [],
        initAddress: null,
        centerMap: null,
        timeout: null,
        proximityWarning: false,
        markerMap: null
    }

    const [state, dispatch] = useReducer(mapAddressFinderReducer, {
        ...stateInitValues,
        touchZoom: !displayOnly,
        doubleClickZoom: !displayOnly,
        scrollWheelZoom: !displayOnly,
        dragging: !displayOnly,
        center: { lat: parseFloat(communication.inc_latitude), lon: parseFloat(communication.inc_longitude) }
    });
    const centerPresentInState = typeof communication.inc_latitude === "number"
    const positionPresent = isPositionPresent(communication) && centerPresentInState



    useEffect(() => {
        if (!isPositionPresent(communication)) {
            return;
        }
        /* istanbul ignore next */
        if (!mapRef) return;

        // Usa HERE Raster Tiles v3 con apiKey (l'utente ha scelto di restare con HERE)
        const apiKey = (window as any).REACT_APP_HERE_API_KEY;

        // Rimuove eventuali layer già presenti creati da esecuzioni precedenti
        try {
            mapRef.eachLayer((layer: any) => {
                // non rimuovere markers creati internamente più avanti
                 /* istanbul ignore next */
                if (layer && layer._url && layer.getAttribution) {
                    try { mapRef.removeLayer(layer); } catch (e) { /* ignore */ }
                }
            });
        } catch (e) { /* ignore */ }

        const _layerProvider: any = L.tileLayer(
            `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?apiKey=${apiKey}`,
            {
                maxZoom: 20,
            }
        );
        _layerProvider.addTo(mapRef);

        const _center: any = { lat: communication.inc_latitude, lng: communication.inc_longitude }
        const newState = {
            initialMap: [communication.inc_latitude, communication.inc_longitude, communication.inc_luogo],
            initAddress: communication.inc_luogo,
            targetPosition: { lat: communication.inc_latitude, lng: communication.inc_longitude },
            centerMap: { lat: communication.inc_latitude, lng: communication.inc_longitude },
            address: communication.inc_luogo,
            center: _center,
            markerMap: displayOnly ? <Marker position={_center} icon={iconCrash}></Marker> : null
        }

        dispatch({ type: MapStateActions.UPDATE_STATE, payload: newState })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [communication?.inc_luogo, mapRef])

    // NOTA: la vecchia funzione getLatLonFromJsonP non serve più (jsonp). rimuoviamo.

    const selectedPosition = (suggestion: any, userSelectedSuggestion?: boolean) => {
        // If user has clicked enter and there were not suggestions, then update only the label (the map will not move)
        if (!userSelectedSuggestion && !suggestion) {
            dispatch({
                type: MapStateActions.UPDATE_STATE, payload: {
                    suggestions: [],
                }
            })
            /* istanbul ignore next */
            mapRef && mapRef.setView({ lat: state.targetPosition.lat, lng: state.targetPosition.lng });
        } else {
            // Else, if a suggestion was present, then execute lookup (v7)
            /* istanbul ignore next */
            const apiKey = (window as any).REACT_APP_HERE_API_KEY;
            /* istanbul ignore next */
            axios.get("https://lookup.search.hereapi.com/v1/lookup", {
                params: {
                    id: extractSuggestionId(suggestion),
                    apiKey: apiKey
                }
            })
                .then((response) => {
                    // response.data è l'item
                    let { Latitude, Longitude, AddressLabel } = getLatLonFromLookupResponse(response.data);

                    // If the user has clicked Enter and there were a suggestion (or if the user selected the suggestion), the label will be set to what the user typed
                    // the lat-lon will be set to the reverse-geocoding value and the map will go to the first suggestion
                    let addressLabel = userSelectedSuggestion ? AddressLabel : state.address;

                    dispatch({
                        type: MapStateActions.UPDATE_STATE, payload: {
                            suggestions: [],
                            targetPosition: { lat: Latitude, lng: Longitude },
                            address: addressLabel,
                            proximityWarning: false
                        }
                    })
                    /* istanbul ignore next */
                    mapRef && mapRef.setView({ lat: Latitude, lng: Longitude });
                })
                .catch((error) => {
                    console.log(error);
                });
        }
    }

    const submitPosition = (event: SyntheticEvent) => {
        event.preventDefault();
        selectedPosition(state.suggestions && state.suggestions[0]);
    }

    const onEnterPress = (e: KeyboardEvent) => {
        if ((e as any).keyCode === 13 && (e as any).shiftKey === false) {
            e.preventDefault();
            submitPosition(e as any);
        }
    };

    /* istanbul ignore next */
    /*const hanldeAddress = (addr: any, stateType: string) => {
        
        if (!addr) return "";
        if (stateType === "reverse") {
            let organizeAddress = (addr.Street || "") + (addr.HouseNumber ? " " + addr.HouseNumber : "") + ", " + (addr.PostalCode || "") + " " + (addr.City || "") + " " + (addr.County || "");
            if (addr.Street === "undefined" || !addr.Street) {
                organizeAddress = (addr.PostalCode || "") + " " + (addr.City || "") + " " + (addr.County || "");
            }
            return organizeAddress;
        }
        if (stateType === "geosearch") {
            let organizeAddress = (addr.street || "") + (addr.houseNumber ? " " + addr.houseNumber : "") + ", " + (addr.postalCode || "") + " " + (addr.city || "") + " " + (addr.county || "");
            if (!addr.houseNumber) {
                organizeAddress = (addr.street || "") + " " + (addr.postalCode || "") + " " + (addr.city || "") + " " + (addr.county || "");
            }
            return organizeAddress;
        }
        return "";
    }*/

    //se digito il testo mi restituisce una serie di suggerimenti possibili
    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const { timeout } = state;
        // proximity string: lat,lon,radius
        const proximityCoords = communication.inc_latitude + "," + communication.inc_longitude;
        clearTimeout(timeout);
        dispatch({ type: MapStateActions.UPDATE_STATE, payload: { address: event.target.value } })

        if (state.address) {
            let elaborateQuery = state.address.trim().replace(/\s+/g, "+");
            dispatch({
                type: MapStateActions.UPDATE_STATE, payload: {
                    timeout: setTimeout(() => {
                        const apiKey = (window as any).REACT_APP_HERE_API_KEY;

                        axios.get("https://autosuggest.search.hereapi.com/v1/autosuggest", {
                            params: {
                                q: elaborateQuery,
                                at: proximityCoords,
                                limit: 10,
                                apiKey: apiKey
                            }
                        })
                            .then((response) => {
                                const items: any[] = response.data.items || [];
                                const elencoSugg: any[] = [];

                                // filtra solo items con posizione (address/position) e calcola distanza dal centro
                                items.forEach((it: any) => {
                                    const pos = it.position;
                                    if (pos && pos.lat && pos.lng) {
                                        const dist = getDistanceMeters(parseFloat(communication.inc_latitude), parseFloat(communication.inc_longitude), pos.lat, pos.lng);
                                        if (dist < (maxDistance || MAX_DISTANCE)) {
                                            // Determino il locationId in modo esplicito e leggibile
                                            let locationId: string | null = null;
                                            if (it.id) {
                                                locationId = it.id;
                                            } else if (it.href) {
                                                locationId = it.href;
                                            } else if (it.position) {
                                                locationId = `${it.position.lat},${it.position.lng}`;
                                            }
                                            elencoSugg.push({
                                                locationId,
                                                elaboratedLabel: (it.address?.label) || it.title || it.vicinity || "",
                                                position: pos,
                                                raw: it
                                            });
                                        }
                                    }
                                });

                                dispatch({
                                    type: MapStateActions.UPDATE_STATE, payload: { suggestions: elencoSugg }
                                });
                            })
                            .catch((error) => {
                                /* istanbul ignore next */
                                console.log(error);
                            });
                    }, 500),
                }
            })

        } else {
            dispatch({
                type: MapStateActions.UPDATE_STATE, payload: { suggestions: [] }
            });
        }
        dispatch({
            type: MapStateActions.UPDATE_STATE, payload: { proximityWarning: true }
        });

    }

    /* istanbul ignore next */
    const hideWarning = () => {
        dispatch({
            type: MapStateActions.UPDATE_STATE, payload: { proximityWarning: false }
        });
    }

    const resetPosition = () => {
        dispatch({
            type: MapStateActions.UPDATE_STATE, payload: {
                suggestions: [],
                targetPosition: { lat: communication.inc_latitude, lng: communication.inc_longitude },
                address: state.initAddress,
            }
        })
        /* istanbul ignore next */
        mapRef && mapRef.setView({ lat: parseFloat(communication.inc_latitude), lng: parseFloat(communication.inc_longitude) }, zoom);
    }

    /* istanbul ignore next */
    const dragEndHandler = (e: any) => {
        const _eTarget: any = (e as any).target;
        if (mapRef && _eTarget.getCenter() !== communication.inc_luogo) {
            const targetPosition = _eTarget.getCenter();
            dispatch({
                type: MapStateActions.UPDATE_STATE, payload: {
                    /* istanbul ignore next */
                    targetPosition: { lat: targetPosition.lat, lng: targetPosition.lng },
                }
            })
            /* istanbul ignore next */
            let _distance = LF.distance(mapRef, state.targetPosition, state.centerMap);
            if (_distance <= (maxDistance || MAX_DISTANCE)) {
                const apiKey = (window as any).REACT_APP_HERE_API_KEY;
                axios.get("https://revgeocode.search.hereapi.com/v1/revgeocode", {
                    params: {
                        at: `${targetPosition.lat},${targetPosition.lng}`,
                        lang: "it-IT",
                        apiKey: apiKey
                    }
                })
                    .then((response) => {
                        /* istanbul ignore next */
                        const probably = response.data.items && response.data.items.length ? response.data.items[0] : null;
                        let organizeAddress = probably && probably.address ? probably.address.label : null;

                        dispatch({
                            type: MapStateActions.UPDATE_STATE, payload: { address: organizeAddress }
                        })
                    })
                    .catch((error) => {
                        /* istanbul ignore next */
                        console.log(error);
                    });
            } else {
                resetPosition();
            }
        }
    }

    const MapEvents = () => {
        /* istanbul ignore next */
        useMapEvents({
            dragend: (e: any) => { dragEndHandler(e) }, zoomend: (e: any) => { dragEndHandler(e) }
        });
        return null;
    }

    const componentMap = (
        <div className={displayOnly ? "parent-stop-events" : "parent-map"}>

            {positionPresent && <MapContainer
                ref={setMap}
                touchZoom={state.touchZoom}
                scrollWheelZoom={state.scrollWheelZoom}
                dragging={state.dragging}
                doubleClickZoom={state.doubleClickZoom}
                zoomControl={false}
                attributionControl={false}
                center={state.center}
                zoom={zoom || 18}
                maxZoom={18}
                minZoom={15}
            >
                {state.markerMap}
                <MapEvents />
            </MapContainer>}
        </div>
    );

    const resumeButton = <div
        className="buttonMap"
        onClick={(e) => {
            resetPosition();
        }}
    >
        <FontAwesomeIcon icon={faTimes} />
    </div>
    const marker: any = <><img className="marker-map-centered" src={MapMarkerImg} alt="map marker" /> </>
    const fakeMarker = fixedMarkerDropMap ? <img className="marker-map-centered fake-marker" src={MapMarkerImg} alt="fake map marker" /> : null;

    const iconCrash = useMemo(() => new L.Icon({
        iconUrl: marker,
        iconSize: new L.Point(60, 60),
        className: "div-icon",
        iconAnchor: [30, 55],
        popupAnchor: [0, -60],
        // eslint-disable-next-line react-hooks/exhaustive-deps
    } as any), [])
    const formSearch = (
        <form onSubmit={submitPosition} className="address-form">
            <div className="form-class">
                <textarea onKeyDown={onEnterPress} placeholder="Inserisci un indirizzo"
                    rows={2} value={state.address} onChange={handleChange} />
                {state.address.length ? (
                    <div className="suggest-list">
                        {state.suggestions.map((suggestion: any, i: number) => (
                            <div className="text-suggestion" key={i} onClick={() => selectedPosition(suggestion, true)}>
                                {suggestion.elaboratedLabel}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
            {hasProximityWarning && state.proximityWarning && (
                <div className="proximity-warning-container" onClick={hideWarning}>
                    <div className="little-cube"></div>
                    <FontAwesomeIcon icon={faTimes} />
                    <div>
                        <FontAwesomeIcon icon={faCircleExclamation} />
                        <p>Puoi cercare un indirizzo solo in prossimità del luogo rilevato.</p>
                    </div>
                </div>
            )}
        </form>
    );
    const labelMap = displayOnly ? (
        <div className="label-map">
            L'incidente risulta avvenuto nei pressi di <strong>{communication.inc_luogo}</strong>?
        </div>) : null

    const _onCancelHandler = () => {
        onCancelHandler()
    }

    const _onConfirmHandler = () => {
        onConfirmHandler({
            latitude: state.targetPosition.lat,
            longitude: state.targetPosition.lng,
            luogo: state.address
        })
    }

    return (
        <>
            <MapPageContainer className={classes} >
                {labelMap}
                {fakeMarker}
                {withSearch && <>
                    {resumeButton}
                    {formSearch}
                </>}
                {componentMap}
                {manageBtnPresent && <ConfirmCancelBtn
                    cancelPresent={true}
                    confirmPresent={true}
                    cancelCallback={() => _onCancelHandler()}
                    confirmCallback={() => _onConfirmHandler()}
                    className="map-modify-confirm" />}
            </MapPageContainer></>
    )
}
export default MapAddressFinder;
